/**
 * Elements not yet in the remote catalog (`assets/json/elements.json`, which
 * is published from the Notion elements database), merged in so a new element
 * can ship before its catalog row exists. Staging and production share that
 * file, so who sees the tile is decided by a feature flag in the add menu
 * (FEATURE_FLAGS.QR_CODE, read in elements-library main-container), not here:
 * an element must not appear before the viewer bundle (static-library) can
 * render it.
 */

import type { StaticEntityType } from "@config";

// The add menu's element shape (elements-library TElement), plus isPro.
type CatalogItem = {
  id: string;
  element: string;
  order: number;
  category: string;
  isPro: boolean;
  json: StaticEntityType;
  previewImage: string | null;
};

const qrBase = {
  name: "QrCode",
  linkType: "url",
  url: "https://www.storydoc.com",
  size: "160",
  centerSize: "22",
  color: "#111111",
  backgroundColor: "#ffffff",
  dotStyle: "square",
  cornerStyle: "square",
  centerType: "none",
  horizontalAlignment: "center",
  clickable: true,
};

const qrItem = (
  id: string,
  order: number,
  settings: Record<string, unknown>,
): CatalogItem => ({
  id,
  // The tile caption; the element type is json.settings.name.
  element: "QR code",
  order,
  category: "Interactive elements",
  isPro: false,
  json: {
    id: `${id}-el`,
    type: "element",
    settings: { ...qrBase, ...settings },
    children: [],
  },
  previewImage: null,
});

export const LOCAL_CATALOG_ELEMENTS: CatalogItem[] = [
  qrItem("local-qr-code", 100, {}),
];

export const withLocalCatalogElements = (
  catalog: Record<string, CatalogItem[]>,
) => {
  if (!catalog || typeof catalog !== "object") return catalog;
  const merged = { ...catalog };
  for (const item of LOCAL_CATALOG_ELEMENTS) {
    // Once the remote catalog carries the element, it wins.
    const name = (item.json.settings as { name: string }).name;
    const remote = catalog[item.category] ?? [];
    if (
      remote.some(
        (existing) =>
          (existing.json?.settings as { name?: string } | undefined)?.name ===
          name,
      )
    )
      continue;
    merged[item.category] = [...(merged[item.category] ?? []), item];
  }
  return merged;
};

/** The QR code tile, local or (later) from the remote catalog. */
export const isQrCodeCatalogItem = (item: {
  json?: { settings?: { name?: unknown } };
}) => item.json?.settings?.name === "QrCode";
